import React, { useState } from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody } from '@repo/ui';
import { showToast } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';

// Import local modules
import {
  TemplateCard,
  UploadTemplateForm,
  TestTemplateForm,
  EmailSenderForm,
  ImageGallery,
  ImagePreviewModal,
} from '../components';
import {
  useSystemTemplates,
  useTenantTemplates,
  useUploadTemplate,
  useDeleteTemplate,
  useTestTemplate,
  useSendEmail,
  useImageAssets,
  useUploadImage,
  useDeleteImage,
} from '../queries';
import type { UploadForm, TestForm, EmailForm, TestResult, EmailResult } from '../types';
import {
  getApiUrl,
  validateField,
  validateJson,
  buildTemplateUploadFormData,
  getFileFromInput,
  getDefaultUploadForm,
} from '../utils';

// Standardized button styles for XTND-Juniper
const BUTTON_STYLES = {
  standard: {
    variant: 'outline' as const,
    size: 'sm' as const,
  },
  primary: {
    variant: 'primary' as const,
    size: 'md' as const,
  },
  secondary: {
    variant: 'secondary' as const,
    size: 'md' as const,
  },
  ghost: {
    variant: 'ghost' as const,
    size: 'sm' as const,
  },
} as const;

const BUTTON_CLASSES = {
  group: 'gap-2',
  backButton: 'mr-4',
} as const;

export const TemplateManagerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upload' | 'library' | 'test' | 'images'>('library');
  const [selectedTemplateType, setSelectedTemplateType] = useState<
    'all' | 'pdf' | 'email' | 'document'
  >('all');

  // Upload form state
  const [uploadForm, setUploadForm] = useState<UploadForm>(getDefaultUploadForm());

  // Test form state
  const [testForm, setTestForm] = useState<TestForm>({
    templateId: '',
    sampleData:
      '{\n  "clientName": "John Doe",\n  "invoiceNumber": "INV-001",\n  "amount": 1250.00,\n  "dueDate": "2024-02-15",\n  "firmName": "XTND Legal"\n}',
  });
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [htmlViewMode, setHtmlViewMode] = useState<'source' | 'rendered'>('source');

  // Email sending state
  const [emailForm, setEmailForm] = useState<EmailForm>({
    toEmail: '',
    subject: '',
    attachment: null,
    secureAttachment: false,
  });
  const [showEmailSender, setShowEmailSender] = useState(false);
  const [lastEmailResult, setLastEmailResult] = useState<EmailResult | null>(null);

  // Image management state
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Fetch system template library
  const { data: systemLibrary, isLoading: libraryLoading } =
    useSystemTemplates(selectedTemplateType);

  // Fetch tenant templates
  const { data: tenantTemplates, isLoading: tenantLoading } =
    useTenantTemplates(selectedTemplateType);

  // Fetch image assets
  const {
    data: imageAssets,
    isLoading: imagesLoading,
    refetch: refetchImages,
  } = useImageAssets();

  // Upload template mutation
  const uploadMutation = useUploadTemplate(() => {
    setUploadForm(getDefaultUploadForm());
  });

  // Upload & Test template mutation
  const uploadAndTestMutation = useUploadTemplate((data: any) => {
    // Clear upload form
    setUploadForm(getDefaultUploadForm());

    // Switch to test tab and populate with new template
    if (data.templateId) {
      setActiveTab('test');
      setTestForm(prev => ({
        ...prev,
        templateId: data.templateId,
      }));
    } else {
      showToast.warning(
        'Template uploaded but could not auto-populate test form. Please select the template manually in the Test tab.'
      );
      setActiveTab('test');
    }
  });

  // Upload image mutation
  const uploadImageMutation = useUploadImage(() => refetchImages());

  // Delete image mutation
  const deleteImageMutation = useDeleteImage(() => refetchImages());

  // Test template mutation
  const testMutation = useTestTemplate((data: any) => {
    if (data) {
      setTestResult(data);
    } else {
      setTestResult(null);
      setHtmlViewMode('source');
    }
  });

  // Send email mutation
  const sendEmailMutation = useSendEmail((data: any) => {
    setLastEmailResult(data);
    setShowEmailSender(false);
    setEmailForm({
      toEmail: '',
      subject: '',
      attachment: null,
      secureAttachment: false,
    });
  });

  // Delete template mutation
  const deleteMutation = useDeleteTemplate();

  const handleTemplateUpload = (uploadAndTest: boolean = false) => {
    const file = getFileFromInput('template-file');

    if (!validateField(file, 'a file', showToast)) return;
    if (!validateField(uploadForm.templateName, 'a template name', showToast)) return;

    const formData = buildTemplateUploadFormData(
      file!,
      uploadForm.templateName,
      uploadForm.templateType,
      uploadForm.description,
      uploadForm.tags
    );

    const mutation = uploadAndTest ? uploadAndTestMutation : uploadMutation;
    mutation.mutate(formData);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('description', 'Uploaded via Template Manager');

    uploadImageMutation.mutate(formData);
  };

  const handleTest = () => {
    if (!validateField(testForm.templateId, 'a template ID', showToast)) return;

    const sampleData = validateJson(testForm.sampleData, showToast);
    if (!sampleData) return;

    testMutation.mutate({
      templateId: testForm.templateId,
      sampleData,
    });
  };

  const handleSendEmail = () => {
    if (!validateField(emailForm.toEmail, 'a recipient email address', showToast)) return;
    if (!validateField(emailForm.subject, 'an email subject', showToast)) return;
    if (!validateField(testForm.templateId, 'a template (test the template first)', showToast))
      return;

    const sampleData = validateJson(testForm.sampleData, showToast);
    if (!sampleData) return;

    sendEmailMutation.mutate({
      templateId: testForm.templateId,
      sampleData,
      toEmail: emailForm.toEmail,
      subject: emailForm.subject,
      attachment: emailForm.attachment || undefined,
      secureAttachment: emailForm.secureAttachment,
    });
  };

  const handleTestClick = (templateId: string) => {
    setTestForm(prev => ({ ...prev, templateId }));
    setActiveTab('test');
  };

  const openImagePreview = (imageId: string) => {
    setPreviewImageId(imageId);
    setIsImageModalOpen(true);
  };

  const getPreviewImage = () => {
    if (!previewImageId || !imageAssets?.images) return null;
    return imageAssets.images.find(img => img.imageId === previewImageId) || null;
  };

  const TABS = [
    { key: 'library', label: 'Template Library', iconName: 'HiClipboardList' },
    { key: 'upload', label: 'Upload Template', iconName: 'HiArrowUp' },
    { key: 'test', label: 'Test Template', iconName: 'HiPlay' },
    { key: 'images', label: 'Images', iconName: 'HiDocumentAdd' },
  ] as const;
  const base = 'flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors';
  const activeClass =
    'bg-xtnd-blue-500 text-white dark:text-xtnd-dark-800 dark:bg-xtnd-blue-500 hover:bg-xtnd-blue-500 dark:hover:bg-xtnd-blue-500';
  const inactiveClass =
    'text-xtnd-dark-500 hover:text-xtnd-dark dark:text-xtnd-dark-200 dark:hover:text-xtnd-blue';

  return (
    <PageLayout
      title="Template Manager"
      description="Upload, test, and manage Handlebars templates for PDF and email generation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
      <div
        role="tablist"
        className="
                  mx-auto self-center
                  max-w-full w-fit
                  flex flex-wrap justify-center gap-1
                  bg-xtnd-dark-50 dark:bg-xtnd-dark-800 border border-xtnd-dark-100 dark:border-xtnd-dark-600
                  p-1 rounded-lg mb-8
                  "
      >
        {TABS.map(({ key, label, iconName }) => {
          const isActive = activeTab === key;
          return (
            <Button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(key as typeof activeTab)}
              size="sm"
              variant="ghost"
              className={`${base} ${isActive ? activeClass : inactiveClass}`}
              leftIcon={<Icon name={iconName} className="h-4 w-4" />}
            >
              {label}
            </Button>
          );
        })}
      </div>

      {/* Template Library Tab */}
      {activeTab === 'library' && (
        <div>
          <div className="flex items-center justify-end mb-6">
            <select
              value={selectedTemplateType}
              onChange={e => setSelectedTemplateType(e.target.value as any)}
              className="px-4 py-2 border border-xtnd-dark-200 rounded-lg bg-white dark:bg-xtnd-dark-800 text-xtnd-dark dark:text-xtnd-white"
            >
              <option value="all">All Templates</option>
              <option value="pdf">PDF Templates</option>
              <option value="email">Email Templates</option>
              <option value="document">Document Templates</option>
            </select>
          </div>

          {/* System Templates Section */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white mb-4 flex items-center">
              <Icon name="HiClipboardList" className="h-5 w-5 mr-2 text-xtnd-blue" />
              System Templates ({systemLibrary?.templates?.length || 0})
            </h3>
            {libraryLoading ? (
              <div className="text-center py-8 text-xtnd-dark-400 dark:text-xtnd-dark-200">
                Loading system templates...
              </div>
            ) : (systemLibrary?.templates?.length ?? 0) > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {systemLibrary?.templates?.map(template => (
                  <TemplateCard
                    key={template.templateId}
                    template={template}
                    isMaster={true}
                    onTest={handleTestClick}
                    onDelete={deleteMutation.mutate}
                    isDeleting={deleteMutation.isPending}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-xtnd-dark-800 dark:text-xtnd-dark-50">
                <Icon name="HiClipboardList" className="h-12 w-12 mx-auto mb-4" />
                <p>
                  No system templates found. System templates are loaded automatically on startup.
                </p>
              </div>
            )}
          </div>

          {/* Tenant Templates Section */}
          <div>
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white mb-4 flex items-center">
              <Icon name="HiDocumentAdd" className="h-5 w-5 mr-2 text-xtnd-blue" />
              Your Templates ({tenantTemplates?.total || 0})
            </h3>
            {tenantLoading ? (
              <div className="text-center py-8 text-xtnd-dark-400 dark:text-xtnd-dark-200">
                Loading your templates...
              </div>
            ) : (tenantTemplates?.templates?.length ?? 0) > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tenantTemplates?.templates?.map(template => (
                  <TemplateCard
                    key={template.templateId}
                    template={template}
                    isMaster={false}
                    onTest={handleTestClick}
                    onDelete={deleteMutation.mutate}
                    isDeleting={deleteMutation.isPending}
                  />
                )) ?? []}
              </div>
            ) : (
              <div className="text-center py-8 text-xtnd-dark-800 dark:text-xtnd-dark-50">
                <Icon name="HiDocumentAdd" className="h-12 w-12 mx-auto mb-4" />
                <p>No templates found. Upload your first template to get started!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <UploadTemplateForm
          uploadForm={uploadForm}
          onUploadFormChange={setUploadForm}
          onUpload={handleTemplateUpload}
          isUploading={uploadMutation.isPending}
          isUploadingAndTesting={uploadAndTestMutation.isPending}
        />
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="space-y-6">
          <TestTemplateForm
            testForm={testForm}
            onTestFormChange={setTestForm}
            onTest={handleTest}
            systemTemplates={systemLibrary?.templates || []}
            tenantTemplates={tenantTemplates?.templates || []}
            isTesting={testMutation.isPending}
          />

          {/* Test Results */}
          {testResult && (
            <div className="flex justify-center">
              <Card className="w-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-xtnd-dark">Test Results</h3>
                    {testResult.templateName && (
                      <div className="text-sm text-xtnd-dark-500">
                        Testing:{' '}
                        <span className="font-medium text-xtnd-dark">
                          {testResult.templateName}
                        </span>
                        {testResult.templateId && (
                          <span className="text-xtnd-dark-300 ml-2">({testResult.templateId})</span>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-xtnd-dark dark:text-xtnd-light mb-2">
                        Template Variables Found:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {testResult.variables && testResult.variables.length > 0 ? (
                          testResult.variables.map((variable, index) => (
                            <code
                              key={index}
                              className="px-2 py-1 bg-xtnd-dark-50 text-xtnd-dark-700 text-sm rounded dark:bg-xtnd-dark-700 dark:text-xtnd-light"
                            >
                              {'{{'}
                              {variable}
                              {'}}'}
                            </code>
                          ))
                        ) : (
                          <span className="text-xtnd-dark-400 italic">No variables found</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-xtnd-dark dark:text-xtnd-light">
                          HTML Output:
                        </h4>
                        <div className="flex items-center bg-xtnd-dark-50 rounded-lg p-1">
                          <Button
                            onClick={() => setHtmlViewMode('source')}
                            size="sm"
                            variant={htmlViewMode === 'source' ? 'primary' : 'ghost'}
                            className={`mr-1 ${
                              htmlViewMode === 'source'
                                ? 'bg-white text-xtnd-dark shadow-sm'
                                : 'text-xtnd-dark-500 hover:text-xtnd-dark'
                            }`}
                            leftIcon={<Icon name="HiHashtag" className="h-4 w-4" />}
                          >
                            Source
                          </Button>
                          <Button
                            onClick={() => setHtmlViewMode('rendered')}
                            size="sm"
                            variant={htmlViewMode === 'rendered' ? 'primary' : 'ghost'}
                            className={`${
                              htmlViewMode === 'rendered'
                                ? 'bg-white text-xtnd-dark shadow-sm'
                                : 'text-xtnd-dark-500 hover:text-xtnd-dark'
                            }`}
                            leftIcon={<Icon name="HiEye" className="h-4 w-4" />}
                          >
                            Rendered
                          </Button>
                        </div>
                      </div>

                      {htmlViewMode === 'source' ? (
                        <pre className="p-4 bg-xtnd-dark-50 border rounded-lg text-sm overflow-auto max-h-96">
                          {testResult.rendered || 'No HTML output available'}
                        </pre>
                      ) : (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="p-2 bg-xtnd-dark-50 border-b text-xs text-xtnd-dark-500 flex items-center justify-between">
                            <span>Rendered Preview (iframe):</span>
                            <Button
                              onClick={() => setHtmlViewMode('source')}
                              {...BUTTON_STYLES.ghost}
                              size="sm"
                              className="text-xtnd-blue hover:text-xtnd-dark text-xs"
                              title="Switch to source view"
                            >
                              View Source
                            </Button>
                          </div>
                          <iframe
                            srcDoc={
                              testResult.rendered ||
                              '<p style="padding: 20px; color: #666;">No HTML output available</p>'
                            }
                            className="w-full h-96 border-0 bg-white"
                            sandbox="allow-same-origin allow-scripts"
                            title="HTML Preview"
                            style={{ minHeight: '384px' }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Email Sending Section - Show for email templates */}
                    {testResult.templateType === 'email' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-xtnd-dark-600">Email Testing:</h4>
                          <Button
                            {...BUTTON_STYLES.standard}
                            onClick={() => setShowEmailSender(!showEmailSender)}
                            leftIcon={<Icon name="HiMail" className="h-4 w-4" />}
                          >
                            {showEmailSender ? 'Hide' : 'Send Test Email'}
                          </Button>
                        </div>

                        {showEmailSender && (
                          <EmailSenderForm
                            emailForm={emailForm}
                            onEmailFormChange={setEmailForm}
                            onSend={handleSendEmail}
                            onCancel={() => setShowEmailSender(false)}
                            isSending={sendEmailMutation.isPending}
                          />
                        )}
                      </div>
                    )}

                    {/* Email Sending Results */}
                    {lastEmailResult && lastEmailResult.details?.emailLogUrl && (
                      <div>
                        <h4 className="font-medium text-xtnd-dark-600 mb-3">Email Sent Successfully:</h4>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-green-800">
                                <strong>To:</strong> {lastEmailResult.details.to}
                              </p>
                              <p className="text-sm text-green-800">
                                <strong>Subject:</strong> {lastEmailResult.details.subject}
                              </p>
                              {lastEmailResult.details.messageId && (
                                <p className="text-sm text-green-600">
                                  <strong>Message ID:</strong> {lastEmailResult.details.messageId}
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <a
                                href={getApiUrl(lastEmailResult.details.emailLogUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <Icon name="HiDownload" className="h-4 w-4 mr-1" />
                                Download Email Log
                              </a>
                              <Button
                                {...BUTTON_STYLES.standard}
                                onClick={() => setLastEmailResult(null)}
                                className="text-xtnd-dark-500"
                                leftIcon={<Icon name="HiTrash" className="h-4 w-4" />}
                              >
                                Clear
                              </Button>
                            </div>
                          </div>

                          <div className="text-xs text-green-600">
                            💡 <strong>LocalStack Email Capture:</strong> The email was sent via
                            LocalStack SES and captured for testing. The download contains the full
                            email content including HTML body, headers, and metadata.
                          </div>

                          {lastEmailResult.details.hasAttachment && (
                            <div className="text-xs text-blue-600">
                              📎 <strong>Attachment:</strong>{' '}
                              {lastEmailResult.details.secureAttachment
                                ? 'Secure attachment included'
                                : 'Attachment included'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {testResult.templateType === 'pdf' && testResult.downloadUrl && (
                      <div>
                        <h4 className="font-medium text-xtnd-dark-600 mb-2">PDF Download:</h4>
                        <div className="space-y-2">
                          <p className="text-sm text-xtnd-dark-500">
                            PDF generated successfully! Click below to download:
                          </p>
                          <a
                            href={getApiUrl(testResult.downloadUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-xtnd-blue text-white rounded-lg hover:bg-xtnd-dark transition-colors"
                          >
                            <Icon name="HiDocumentAdd" className="h-4 w-4 mr-2" />
                            Download Test PDF
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <ImageGallery
          images={imageAssets?.images || []}
          isLoading={imagesLoading}
          onImageUpload={handleImageUpload}
          onImagePreview={openImagePreview}
          onImageDelete={deleteImageMutation.mutate}
          isDeleting={deleteImageMutation.isPending}
          isUploading={uploadImageMutation.isPending}
        />
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        image={getPreviewImage()}
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
      />
      </div>
    </PageLayout>
  );
};
